import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react';

import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { Meta, breadcrumbLd } from '../seo/Meta.jsx';
import { useLenis } from '../anim/useLenis.js';

const API_BASE = '';

export default function Status() {
  useLenis();
  const [components, setComponents] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/v1/status`)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((d) => setComponents(d.components || []))
      .catch((e) => setErr(e.message));
  }, []);

  const allUp = components && components.every((c) => c.last_status === 'up');

  return (
    <>
      <Meta
        title="System status"
        description="Real-time system status for tender-api.com. Portal uptime, API health, and incident history."
        path="/status"
        jsonLd={[
          breadcrumbLd([
            { name: 'tender-api', path: '/' },
            { name: 'Status', path: '/status' },
          ]),
        ]}
      />
      <Header />
      <main className="container" style={{ paddingTop: 64, paddingBottom: 96 }}>
        <h1 className="hero-headline mono" style={{ fontSize: 'clamp(32px, 4vw, 48px)' }}>
          System status
        </h1>

        {/* Overall status banner */}
        {components && (
          <div
            style={{
              marginTop: 24,
              padding: '16px 20px',
              borderRadius: 8,
              background: allUp ? 'var(--success-bg, rgba(34,197,94,0.08))' : 'var(--warn-bg, rgba(234,179,8,0.08))',
              border: `1px solid ${allUp ? 'var(--success, #22c55e)' : 'var(--warn, #eab308)'}`,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 15,
              fontWeight: 500,
            }}
          >
            {allUp ? (
              <>
                <CheckCircle2 size={18} style={{ color: 'var(--success, #22c55e)' }} />
                All systems operational
              </>
            ) : (
              <>
                <AlertTriangle size={18} style={{ color: 'var(--warn, #eab308)' }} />
                Some systems experiencing issues
              </>
            )}
          </div>
        )}

        {!components && !err && (
          <div style={{ marginTop: 32, color: 'var(--fg-3)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <Loader2 size={16} className="spin" /> loading status...
          </div>
        )}

        {err && (
          <div style={{ marginTop: 32, color: 'var(--danger, #ef4444)' }}>
            Status endpoint unreachable: {err}
          </div>
        )}

        {components && components.length > 0 && (
          <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Header */}
            <div
              className="mono"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 100px 100px 120px',
                padding: '10px 16px',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--fg-3)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span>Component</span>
              <span style={{ textAlign: 'center' }}>Status</span>
              <span style={{ textAlign: 'right' }}>Latency</span>
              <span style={{ textAlign: 'right' }}>30d uptime</span>
            </div>

            {components.map((c) => (
              <div
                key={c.slug}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 100px 100px 120px',
                  padding: '14px 16px',
                  borderBottom: '1px solid var(--border)',
                  alignItems: 'center',
                  fontSize: 14,
                }}
              >
                <div>
                  <span style={{ fontWeight: 500 }}>{c.name}</span>
                  <span className="mono" style={{ color: 'var(--fg-3)', marginLeft: 8, fontSize: 11 }}>
                    {c.kind}
                  </span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <StatusIcon status={c.last_status} />
                </div>
                <div className="mono" style={{ textAlign: 'right', color: 'var(--fg-3)', fontSize: 13 }}>
                  {c.last_latency_ms != null ? `${c.last_latency_ms}ms` : '--'}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <UptimeBar pct={c.uptime_pct_30d} />
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mono" style={{ marginTop: 32, fontSize: 12, color: 'var(--fg-3)' }}>
          Checked every 5 minutes. Rolling 30-day window.
        </p>
      </main>
      <Footer />
    </>
  );
}

function StatusIcon({ status }) {
  if (status === 'up') {
    return <CheckCircle2 size={16} style={{ color: 'var(--success, #22c55e)' }} />;
  }
  if (status === 'degraded') {
    return <AlertTriangle size={16} style={{ color: 'var(--warn, #eab308)' }} />;
  }
  if (status === 'down') {
    return <XCircle size={16} style={{ color: 'var(--danger, #ef4444)' }} />;
  }
  return <span style={{ color: 'var(--fg-3)' }}>--</span>;
}

function UptimeBar({ pct }) {
  const color =
    pct >= 99.9
      ? 'var(--success, #22c55e)'
      : pct >= 99
        ? 'var(--warn, #eab308)'
        : 'var(--danger, #ef4444)';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
      <div
        style={{
          width: 60,
          height: 6,
          borderRadius: 3,
          background: 'var(--bg-2, rgba(0,0,0,0.1))',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.min(pct, 100)}%`,
            height: '100%',
            borderRadius: 3,
            background: color,
            transition: 'width 0.6s ease',
          }}
        />
      </div>
      <span className="mono" style={{ fontSize: 12, color, fontWeight: 500, minWidth: 42, textAlign: 'right' }}>
        {pct > 0 ? `${pct.toFixed(1)}%` : '--'}
      </span>
    </div>
  );
}
